import type { Story, StoryQuest } from '@mezha/api'
import { DIRS, type MapCell, type WorldMap } from './map/geometry'

/** Preserve server order within each group; old optional errands must not hide a new act. */
export function prioritizeStoryQuests(quests: StoryQuest[]): StoryQuest[] {
  return [...quests.filter(q => !q.chapter.endsWith('_SIDE')), ...quests.filter(q => q.chapter.endsWith('_SIDE'))]
}

export function currentStoryQuest(story: Story | undefined): StoryQuest | null {
  const quests = story?.quests ?? []
  const main = quests.filter(q => !q.chapter.endsWith('_SIDE'))
  return main.find(q => q.status === 'active') ?? main.find(q => q.status === 'completed')
    ?? quests.find(q => q.status === 'active') ?? quests.find(q => q.status === 'completed') ?? null
}

/** The first story quest that can currently lead the player somewhere. */
export function activeStoryQuest(story: Story | undefined): StoryQuest | null {
  return prioritizeStoryQuests(story?.quests ?? []).find(q => q.status === 'active' && q.current) ?? null
}

/**
 * A shortest route over the cells that are already present in the world snapshot.
 * The start is omitted and the goal is included, so route[0] is always the next press.
 */
export function questRoute(map: WorldMap, from: { x: number; y: number }, to: { x: number; y: number }): MapCell[] | null {
  if (from.x === to.x && from.y === to.y) return []
  const goal = map.at(to.x, to.y)
  if (!goal || !goal.passable || goal.locked || !map.at(from.x, from.y)) return null

  const start = `${from.x},${from.y}`
  const finish = `${to.x},${to.y}`
  const queue = [start]
  const seen = new Set([start])
  const previous = new Map<string, string>()

  for (let i = 0; i < queue.length; i++) {
    const [x, y] = queue[i].split(',').map(Number)
    const ordered = Object.values(DIRS).slice().sort((a, b) => {
      const ad = Math.abs(to.x - (x + a.dx)) + Math.abs(to.y - (y + a.dy))
      const bd = Math.abs(to.x - (x + b.dx)) + Math.abs(to.y - (y + b.dy))
      return ad - bd
    })
    for (const d of ordered) {
      const cell = map.at(x + d.dx, y + d.dy)
      if (!cell || !cell.passable || cell.locked) continue
      const key = `${cell.x},${cell.y}`
      if (seen.has(key)) continue
      seen.add(key)
      previous.set(key, queue[i])
      if (key === finish) {
        const keys = [key]
        while (keys[0] !== start) keys.unshift(previous.get(keys[0])!)
        return keys.slice(1).map(k => {
          const [cx, cy] = k.split(',').map(Number)
          return map.at(cx, cy)!
        })
      }
      queue.push(key)
    }
  }
  return null
}
